// src/components/admin/Admin.js - Updated with Reporting Dashboard
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useRoles } from '../../hooks/useRoles';
import TenantManagement from './TenantManagement';
import UserManagement from './UserManagement';
import GlobalContentManagement from './GlobalContentManagement';
import ReportingDashboard from '../reporting/ReportingDashboard'; // New import
import EnvironmentBadge from '../EnvironmentBadge';
import '../../styles/globals.css';
import './Admin.css';

const Admin = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  
  const { 
    userProfile, 
    hasAdminAccess, 
    isSuperAdmin, 
    isAdmin, 
    isContentManager,
    isSuperContentManager
  } = useRoles();

  useEffect(() => {
    // Check if user has admin access once profile is loaded
    if (userProfile && !hasAdminAccess()) {
      console.warn('User does not have admin access, redirecting...');
      navigate('/dashboard');
      return;
    }
  }, [userProfile, navigate, hasAdminAccess]);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/');
    }
  };

  if (!userProfile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading admin interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="container mx-auto px-4">
          <div className="flex flex-between py-4">
            <div className="flex gap-6" style={{ alignItems: 'center' }}>
              <Link to="/dashboard" className="text-2xl font-bold --color-brand-scarlet">
                {userProfile?.tenant?.name || 'Oracy Coach'}
              <EnvironmentBadge />
              </Link>
              <span className="text-gray-400">|</span>
              <h1 className="text-xl font-semibold text-gray-800">Administration</h1>
            </div>
            
            <div className="flex gap-4" style={{ alignItems: 'center' }}>
              <span className="text-sm text-gray-600">
                {userProfile.displayName}
              </span>
              <div className="flex gap-2">
                <Link to="/dashboard" className="btn btn-secondary btn-sm">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

<div className="container mx-auto px-4 py-8">
        <div className="admin-main-layout">
          {/* Sidebar Navigation */}
          <div className="admin-sidebar">
            <div className="py-2">
              <button
                onClick={() => setActiveSection('overview')}
                className={`admin-nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              >
                <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                  <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                Overview
              </button>

              {/* Reporting Section */}
              {(isSuperAdmin() || isAdmin() || isSuperContentManager()) && (
                <button
                  onClick={() => setActiveSection('reporting')}
                  className={`admin-nav-item ${activeSection === 'reporting' ? 'active' : ''}`}
                >
                  <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                    <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  Reporting
                </button>
              )}

              {isSuperAdmin() && (
                <button
                  onClick={() => setActiveSection('tenants')}
                  className={`admin-nav-item ${activeSection === 'tenants' ? 'active' : ''}`}
                >
                  <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                    <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  Tenant Management
                </button>
              )}

              {(isAdmin() || isSuperAdmin()) && (
                <button
                  onClick={() => setActiveSection('users')}
                  className={`admin-nav-item ${activeSection === 'users' ? 'active' : ''}`}
                >
                  <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                    <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  User Management
                </button>
              )}

              {/* Global Content Management Section */}
              {(isSuperContentManager() || isSuperAdmin()) && (
                <button
                  onClick={() => setActiveSection('global-content')}
                  className={`admin-nav-item ${activeSection === 'global-content' ? 'active' : ''}`}
                >
                  <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                    <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  Global Content
                </button>
              )}

              {(isContentManager() || isSuperAdmin()) && (
                <button
                  onClick={() => setActiveSection('content')}
                  className={`admin-nav-item ${activeSection === 'content' ? 'active' : ''}`}
                  disabled
                >
                  <div className="icon-container gray" style={{width: '20px', height: '20px', margin: '0 var(--spacing-2) 0 0'}}>
                    <svg style={{width: '16px', height: '16px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  Tenant Content
                  <span className="text-xs text-gray-400 ml-2">(Coming Soon)</span>
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
            {activeSection === 'overview' && (
              <AdminOverview 
                userProfile={userProfile} 
                isSuperAdmin={isSuperAdmin} 
                isAdmin={isAdmin} 
                isContentManager={isContentManager}
                isSuperContentManager={isSuperContentManager}
              />
            )}

            {activeSection === 'reporting' && (isSuperAdmin() || isAdmin() || isSuperContentManager()) && (
              <ReportingDashboard />
            )}

            {activeSection === 'tenants' && isSuperAdmin() && (
              <TenantManagement />
            )}

            {activeSection === 'users' && (isAdmin() || isSuperAdmin()) && (
              <UserManagement />
            )}

            {activeSection === 'global-content' && (isSuperContentManager() || isSuperAdmin()) && (
              <GlobalContentManagement />
            )}

            {activeSection === 'content' && (isContentManager() || isSuperAdmin()) && (
              <ComingSoonSection 
                title="Tenant Content Management"
                description="Tenant-specific content management functionality will be available in a future update."
                icon="content"
              />
            )}
          </div>
        </div>
      </div>
  );
};

// Admin Overview Component (refactored) - for Admin.js
const AdminOverview = ({ userProfile, isSuperAdmin, isAdmin, isContentManager, isSuperContentManager }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  const getPrimaryRole = () => {
    if (!userProfile?.roles) return 'User';
    
    const roles = userProfile.roles;
    if (roles.includes('SuperAdmin') || roles.includes('superAdmin')) return 'Super Administrator';
    if (roles.includes('SuperContentManager')) return 'Super Content Manager';
    if (roles.includes('Admin')) return 'Administrator';
    if (roles.includes('ContentManager')) return 'Content Manager';
    return 'User';
  };

  const getPrimaryRoleColor = () => {
    if (isSuperAdmin()) return 'bg-purple-100 text-purple-700';
    if (isSuperContentManager()) return 'bg-green-100 text-green-700';
    if (isAdmin()) return 'bg-blue-100 text-blue-700';
    if (isContentManager()) return 'bg-orange-100 text-orange-600';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="admin-overview">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Admin Overview</h2>
      
      {/* Current User Card */}
      <div className="card mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Current User</h3>
        <div className="flex flex-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-800 mb-2">
              {userProfile?.displayName || userProfile?.email || 'Unknown User'}
            </h4>
            {userProfile?.displayName && userProfile?.email && (
              <p className="text-sm text-gray-600 mb-3">{userProfile.email}</p>
            )}
            
            {/* Primary Role Badge */}
            <div className="mb-3">
              <span className={`status-pill ${getPrimaryRoleColor()}`}>
                {getPrimaryRole()}
              </span>
            </div>
            
            {/* All Roles (if user has multiple roles) */}
            {userProfile?.roles && userProfile.roles.length > 1 && (
              <div>
                <p className="text-sm text-gray-600 mb-2">All Roles:</p>
                <div className="flex gap-2 flex-wrap">
                  {userProfile.roles.map(role => (
                    <span key={role} className="status-pill active text-xs">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-600">Account Created</p>
            <p className="text-sm font-medium text-gray-800">
              {formatDate(userProfile?.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Current Tenant Info */}
      {userProfile?.tenant && (
        <div className="card mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Organization</h3>
          <div className="flex flex-between">
            <div>
              <h4 className="font-medium text-gray-800">{userProfile.tenant.name}</h4>
              <p className="text-sm text-gray-600">Slug: {userProfile.tenant.slug}</p>
            </div>
            <div className="text-right">
              <span className="status-pill active">Active</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

AdminOverview.propTypes = {
  userProfile: PropTypes.shape({
    displayName: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    createdAt: PropTypes.string,
    tenant: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string
    })
  }).isRequired,
  isSuperAdmin: PropTypes.func.isRequired,
  isAdmin: PropTypes.func.isRequired,
  isContentManager: PropTypes.func.isRequired,
  isSuperContentManager: PropTypes.func.isRequired
};

// Coming Soon Section Component (unchanged)
const ComingSoonSection = ({ title, description, icon }) => {
  const getIcon = () => {
    switch (icon) {
      case 'users':
        return (
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      case 'content':
        return (
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
    }
  };

  return (
    <div>
      <div className="flex flex-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
        <button className="btn btn-primary" disabled>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add {title.split(' ')[0]}
        </button>
      </div>

      <div className="card text-center py-12">
        <div className="icon-container gray">
          {getIcon()}
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          {title} Coming Soon
        </h3>
        <p className="text-gray-500 mb-4">
          {description}
        </p>
      </div>
    </div>
  );
};

ComingSoonSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.oneOf(['users', 'content']).isRequired
};

export default Admin;